import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Spin,
  DatePicker,
  Button,
  message,
  Row,
  Col,
  Select,
  Input,
  Tag,
} from "antd";
import {
  DownloadOutlined,
  FilterOutlined,
  ClearOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import API from "../../../api";
import urls from "../../../api/urls";
import moment from "moment";
import { dateFormat_dmy } from "../../../utils";
import TableContainer from "../../../components/TableContainer";
import constants from "../../../constants/constants";
import { generateDynamicColumns } from "../../../utils/columnGenerator";
import useReportFilters from "../../../hooks/useReportFilters";
import "./style.scss";

const { RangePicker } = DatePicker;
const {
  CP_PENDENCY_REPORT,
  CP_PENDENCY_DESTINATION_HUB_LIST,
  CP_PENDENCY_DELIVERY_CP_LIST,
  CP_PENDENCY_REPORT_DOWNLOAD,
} = urls;
const { Option } = Select;
const { ROLES } = constants;
const { CP_ADMIN, CP_OPERATOR, CP_BOOKINGONLY } = ROLES;

const MOVEMENT_TYPES = ["FORWARD", "REVERSE"];
const SERVICE_TYPES = ["FAST TRACK", "STANDARD", "VALUE PLUS"];
const DOCUMENT_TYPES = ["DOX", "NON-DOX"];
const TRAVEL_TYPES = ["AIR", "SURFACE"];
const IS_CLOSED_OPTIONS = ["YES", "NO", "STANDBY"];
const LAST_TERMINAL_STATUS_OPTIONS = ["RTO_DELIVERED", "DELIVERED"];

const disabledDate = (current) => {
  return current && current > moment().endOf("day");
};

const CPPendancyReport = () => {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const {
    filters,
    debouncedFilters,
    handleFilterChange,
    handlePageChange,
    pageNum,
    pageSize,
    setFilters,
    setPageNum,
  } = useReportFilters(
    {
      destination_hub_id: undefined,
      channel_partner: undefined,
      status: undefined,
      tracking_id: undefined,
      movement_type: undefined,
      service_type: undefined,
      document_type: undefined,
      travel_type: undefined,
      is_closed: "NO",
      last_terminal_status: undefined,
    },
    500,
  );

  const [total, setTotal] = useState(0);
  const [returnedRecords, setReturnedRecords] = useState("-");
  const [bookingDateRange, setBookingDateRange] = useState({
    startDate: moment(),
    endDate: moment(),
  });
  const [statusDate, setStatusDate] = useState(null);
  const [lastTerminalStatusDate, setLastTerminalStatusDate] = useState(null);
  const [destinationHubs, setDestinationHubs] = useState([]);
  const [channelPartners, setChannelPartners] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [refreshSchedule, setRefreshSchedule] = useState(null);

  const userinfo = JSON.parse(localStorage.getItem("userinfo") || "{}");
  const isCP =
    [CP_ADMIN, CP_OPERATOR, CP_BOOKINGONLY].includes(userinfo?.role) ||
    userinfo?.roles?.some((role) =>
      [CP_ADMIN, CP_OPERATOR, CP_BOOKINGONLY].includes(role),
    );

  useEffect(() => {
    fetchRefreshSchedule();
    if (isCP) {
      const userIdData = JSON.parse(localStorage.getItem("userId") || "{}");
      const premiseName = userIdData?.premises?.premiseName;
      if (premiseName) {
        setFilters((prev) => ({ ...prev, channel_partner: premiseName }));
      }
    }
  }, [isCP, userinfo?.name, filters.channel_partner]);

  const fetchRefreshSchedule = async () => {
    try {
      const res = await API.Api(urls.REFRESH_SCHEDULE).get();
      if (res?.response?.data?.data?.[0]) {
        setRefreshSchedule(res.response.data.data[0]);
      }
    } catch (error) {
      console.error("Error fetching refresh schedule:", error);
    }
  };

  const fetchReportData = async (page, size) => {
    setLoading(true);
    try {
      const payload = {
        user_name: "laxminandan",
        user_email: "laxminandan.handique@shreemaruti.com",
      };

      const queryParamsObject = { page, size };

      if (bookingDateRange?.startDate && bookingDateRange?.endDate) {
        queryParamsObject.fromDate_booking_date_time =
          bookingDateRange.startDate.format("YYYY-MM-DD");
        queryParamsObject.toDate_booking_date_time =
          bookingDateRange.endDate.format("YYYY-MM-DD");
      }
      if (statusDate?.startDate && statusDate?.endDate) {
        queryParamsObject.fromDate_status_time =
          statusDate.startDate.format("YYYY-MM-DD");
        queryParamsObject.toDate_status_time =
          statusDate.endDate.format("YYYY-MM-DD");
      }
      if (
        lastTerminalStatusDate?.startDate &&
        lastTerminalStatusDate?.endDate
      ) {
        queryParamsObject.fromDate_last_terminal_status_time =
          lastTerminalStatusDate.startDate.format("YYYY-MM-DD");
        queryParamsObject.toDate_last_terminal_status_time =
          lastTerminalStatusDate.endDate.format("YYYY-MM-DD");
      }
      if (debouncedFilters.destination_hub_id)
        queryParamsObject.destination_hub_id =
          debouncedFilters.destination_hub_id;
      if (debouncedFilters.channel_partner)
        queryParamsObject.channel_partner = debouncedFilters.channel_partner;
      if (debouncedFilters.status)
        queryParamsObject.status = debouncedFilters.status;
      if (debouncedFilters.tracking_id)
        queryParamsObject.tracking_id = debouncedFilters.tracking_id;
      if (debouncedFilters.movement_type)
        queryParamsObject.movement_type = debouncedFilters.movement_type;
      if (debouncedFilters.service_type)
        queryParamsObject.service_type = debouncedFilters.service_type;
      if (debouncedFilters.document_type)
        queryParamsObject.document_type = debouncedFilters.document_type;
      if (debouncedFilters.travel_type)
        queryParamsObject.travel_type = debouncedFilters.travel_type;
      if (debouncedFilters.is_closed)
        queryParamsObject.is_closed = debouncedFilters.is_closed;
      if (debouncedFilters.last_terminal_status)
        queryParamsObject.last_terminal_status =
          debouncedFilters.last_terminal_status;

      const queryParams = new URLSearchParams(queryParamsObject);
      const result = await API.Api(
        `${CP_PENDENCY_REPORT}?${queryParams.toString()}`,
      ).postCustomHeader(payload, {
        Authorization:
          "Bearer e50d772343d81ea31b24cfca1ca22755a1616e2f19a6051ce994d0329ddc03c4",
      });

      const response = result?.response?.data;
      if (response?.success) {
        setTableData(response.data || []);
        setTotal(response.total_records || 0);
        setReturnedRecords(response.returned_records || 0);
      } else {
        setTableData([]);
        setTotal(0);
        message.error(response?.message || "Failed to fetch data");
      }
    } catch (error) {
      console.error("Error fetching CP Pendancy Report:", error);
      message.error("Error fetching report data");
      setTableData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (tableData.length === 0) {
      message.error("No data available to download");
      return;
    }
    const queryParamsObject = {};
    if (bookingDateRange?.startDate && bookingDateRange?.endDate) {
      queryParamsObject.bookingFromDate =
        bookingDateRange.startDate.format("YYYY-MM-DD");
      queryParamsObject.bookingToDate =
        bookingDateRange.endDate.format("YYYY-MM-DD");
    }
    if (statusDate?.startDate && statusDate?.endDate) {
      queryParamsObject.statusTimeFromDate =
        statusDate.startDate.format("YYYY-MM-DD");
      queryParamsObject.statusTimeToDate =
        statusDate.endDate.format("YYYY-MM-DD");
    }
    if (lastTerminalStatusDate?.startDate && lastTerminalStatusDate?.endDate) {
      queryParamsObject.lastTerminalStatusTimeFromDate =
        lastTerminalStatusDate.startDate.format("YYYY-MM-DD");
      queryParamsObject.lastTerminalStatusTimeToDate =
        lastTerminalStatusDate.endDate.format("YYYY-MM-DD");
    }
    if (filters.destination_hub_id)
      queryParamsObject.destinationHubId = filters.destination_hub_id;
    if (filters.channel_partner)
      queryParamsObject.channelPartner = filters.channel_partner;
    if (filters.status) queryParamsObject.status = filters.status;
    if (filters.tracking_id) queryParamsObject.trackingId = filters.tracking_id;
    if (filters.movement_type)
      queryParamsObject.movementType = filters.movement_type;
    if (filters.service_type)
      queryParamsObject.serviceType = filters.service_type;
    if (filters.document_type)
      queryParamsObject.documentType = filters.document_type;
    if (filters.travel_type) queryParamsObject.travelType = filters.travel_type;
    if (filters.is_closed) queryParamsObject.isClosed = filters.is_closed;
    if (filters.last_terminal_status)
      queryParamsObject.lastTerminalStatus = filters.last_terminal_status;

    const queryParams = new URLSearchParams(queryParamsObject);
    window.open(
      `${CP_PENDENCY_REPORT_DOWNLOAD}?${queryParams.toString()}`,
      "_blank",
    );
  };

  const fetchDestinationHubs = async () => {
    try {
      const result = await API.Api(CP_PENDENCY_DESTINATION_HUB_LIST).get();
      const response = result?.response?.data;
      if (result?.status && response) {
        setDestinationHubs(
          Array.isArray(response) ? response : response.data || [],
        );
      } else {
        message.error(
          response?.message || "Failed to fetch destination hub list",
        );
      }
    } catch (error) {
      console.error("Error fetching destination hubs:", error);
    }
  };

  const fetchChannelPartners = async () => {
    try {
      const result = await API.Api(CP_PENDENCY_DELIVERY_CP_LIST).get();
      const response = result?.response?.data;
      if (result?.status && response) {
        setChannelPartners(
          Array.isArray(response) ? response : response.data || [],
        );
      } else {
        message.error(
          response?.message || "Failed to fetch channel partner list",
        );
      }
    } catch (error) {
      console.error("Error fetching channel partners:", error);
    }
  };

  useEffect(() => {
    fetchDestinationHubs();
    if (!isCP) fetchChannelPartners();
  }, [isCP]);

  useEffect(() => {
    const uniqueStatuses = Array.from(
      new Set(
        (tableData || [])
          .map((item) => item.status)
          .filter((status) => status && status !== ""),
      ),
    );
    setStatusOptions(uniqueStatuses);
  }, [tableData]);

  useEffect(() => {
    if (isCP && !debouncedFilters.channel_partner) return;
    fetchReportData(pageNum, pageSize);
  }, [
    pageNum,
    pageSize,
    debouncedFilters,
    bookingDateRange,
    statusDate,
    lastTerminalStatusDate,
    isCP,
  ]);

  const handleBookingDateChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setBookingDateRange({ startDate: dates[0], endDate: dates[1] });
    } else {
      setBookingDateRange(null);
    }
  };

  const handleStatusDateChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setStatusDate({ startDate: dates[0], endDate: dates[1] });
    } else {
      setStatusDate(null);
    }
  };

  const handleLastTerminalStatusDateChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setLastTerminalStatusDate({ startDate: dates[0], endDate: dates[1] });
    } else {
      setLastTerminalStatusDate(null);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      destination_hub_id: undefined,
      channel_partner: isCP ? filters.channel_partner : undefined,
      status: undefined,
      tracking_id: undefined,
      movement_type: undefined,
      service_type: undefined,
      document_type: undefined,
      travel_type: undefined,
      is_closed: "NO",
      last_terminal_status: undefined,
    });
    setStatusDate(null);
    setLastTerminalStatusDate(null);
    setPageNum(1);
  };

  const activeFilterCount = useMemo(() => {
    return (
      Object.values(filters).filter(Boolean).length +
      (statusDate ? 1 : 0) +
      (lastTerminalStatusDate ? 1 : 0)
    );
  }, [filters, statusDate, lastTerminalStatusDate]);

  const staticColumns = [
    {
      title: "Tracking ID",
      dataIndex: "tracking_id",
      key: "tracking_id",
      width: 150,
      render: (text) => (
        <span
          style={{ fontWeight: 600, color: "#1a73e8", cursor: "pointer" }}
          onClick={() => {
            if (text && text !== "-") {
              navigate("/shipment-tracker", {
                state: { awb: text, num: Math.random() },
              });
            }
          }}
        >
          {text || "-"}
        </span>
      ),
    },
    {
      title: "Booking Date Time",
      dataIndex: "booking_date_time",
      key: "booking_date_time",
      width: 180,
      render: (text) =>
        text ? moment(text).format("DD-MM-YYYY hh:mm A") : "-",
    },
    {
      title: "Status Time",
      dataIndex: "status_time",
      key: "status_time",
      width: 180,
      render: (text) =>
        text ? moment(text).format("DD-MM-YYYY hh:mm A") : "-",
    },
    {
      title: "Last Terminal Status Time",
      dataIndex: "last_terminal_status_time",
      key: "last_terminal_status_time",
      width: 200,
      render: (text) =>
        text ? moment(text).format("DD-MM-YYYY hh:mm A") : "-",
    },
    {
      title: "Outscan by Hub At",
      dataIndex: "outscan_by_hub_at",
      key: "outscan_by_hub_at",
      width: 200,
      render: (text) =>
        text ? moment(text).format("DD-MM-YYYY hh:mm A") : "-",
    },
    {
      title: "Inscan by CP At",
      dataIndex: "inscan_by_cp_at",
      key: "inscan_by_cp_at",
      width: 200,
      render: (text) =>
        text ? moment(text).format("DD-MM-YYYY hh:mm A") : "-",
    },
  ];

  const columns = useMemo(
    () => generateDynamicColumns(tableData, staticColumns),
    [tableData],
  );

  return (
    <div className="cp-pendancy-report-container">
      {/* ── Page Top Bar ── */}
      <div className="page-topbar">
        <div className="topbar-left">
          <span className="title-main">CP Pendancy Report</span>
          {refreshSchedule && (
            <div className="sync-indicators">
              <div className="sync-tag last-refresh">
                <span className="sync-label">Last Refresh</span>
                <span className="sync-time">
                  {moment(refreshSchedule.dag_last_completion_time_ist).format(
                    "DD-MM-YYYY | HH:mm:ss",
                  )}
                </span>
              </div>
              <div className="sync-tag next-sync">
                <span className="sync-label">Next Sync</span>
                <span className="sync-time">
                  {moment(refreshSchedule.dag_next_run_ist).format(
                    "DD-MM-YYYY | HH:mm:ss",
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="topbar-actions">
          <div className="filter-item">
            <span className="filter-label">Booking Date</span>
            <RangePicker
              value={
                bookingDateRange
                  ? [bookingDateRange.startDate, bookingDateRange.endDate]
                  : undefined
              }
              format={dateFormat_dmy}
              onChange={handleBookingDateChange}
              disabledDate={disabledDate}
              allowClear
            />
          </div>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            className="download-btn"
          >
            Download
          </Button>
        </div>
      </div>

      {/* ── Summary Hero Band ── */}
      <div className="summary-hero-band">
        <div className="hero-left">
          <div className="hero-icon">
            <FileTextOutlined />
          </div>
          <div className="hero-text">
            <div className="hero-label">CP Pendancy Overview</div>
            <div className="hero-desc">
              Shipments pending delivery at channel partner level
            </div>
          </div>
        </div>
        <div className="hero-stats">
          <div className="hero-stat">
            <div className="hero-stat-value">
              {typeof total === "number" ? total.toLocaleString() : total}
            </div>
            <div className="hero-stat-label">Total Records</div>
          </div>
          <div className="hero-divider" />
          <div className="hero-stat">
            <div className="hero-stat-value">
              {typeof returnedRecords === "number"
                ? returnedRecords.toLocaleString()
                : returnedRecords}
            </div>
            <div className="hero-stat-label">Showing</div>
          </div>
        </div>
      </div>

      {/* ── Filters Panel ── */}
      <div className="filters-panel">
        <div className="filters-header">
          <FilterOutlined className="filter-icon" />
          <span className="filters-title">Filters</span>
          {activeFilterCount > 0 && (
            <Tag color="blue" className="active-filters-tag">
              {activeFilterCount} active
            </Tag>
          )}
          {activeFilterCount > 0 && (
            <Button
              size="small"
              icon={<ClearOutlined />}
              onClick={handleClearFilters}
              className="clear-filters-btn"
            >
              Clear All
            </Button>
          )}
        </div>
        <div className="filters-body">
          <Row gutter={[16, 14]}>
            <Col xs={24} sm={12} md={8} lg={6} xl={4} className="filter-col">
              <span className="filter-field-label">Status Time</span>
              <RangePicker
                value={
                  statusDate
                    ? [statusDate.startDate, statusDate.endDate]
                    : undefined
                }
                format={dateFormat_dmy}
                onChange={handleStatusDateChange}
                disabledDate={disabledDate}
                allowClear
                style={{ width: "100%" }}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6} xl={4} className="filter-col">
              <span className="filter-field-label">
                Last Terminal Status Time
              </span>
              <RangePicker
                value={
                  lastTerminalStatusDate
                    ? [lastTerminalStatusDate.startDate, lastTerminalStatusDate.endDate]
                    : undefined
                }
                format={dateFormat_dmy}
                onChange={handleLastTerminalStatusDateChange}
                disabledDate={disabledDate}
                allowClear
                style={{ width: "100%" }}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6} xl={4} className="filter-col">
              <span className="filter-field-label">Destination Hub</span>
              <Select
                allowClear
                showSearch
                placeholder="Select hub"
                style={{ width: "100%" }}
                value={filters.destination_hub_id}
                onChange={(value) =>
                  handleFilterChange("destination_hub_id", value)
                }
                optionFilterProp="children"
              >
                {destinationHubs.map((hub, idx) => {
                  const label =
                    typeof hub === "object" ? hub.destination_hub : hub;
                  const value =
                    typeof hub === "object" ? hub.destination_hub_id : hub;
                  return (
                    <Option key={value || idx} value={value}>
                      {label}
                    </Option>
                  );
                })}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6} xl={4} className="filter-col">
              <span className="filter-field-label">Channel Partner</span>
              <Select
                allowClear={!isCP}
                showSearch
                placeholder="Select CP"
                style={{ width: "100%" }}
                value={filters.channel_partner}
                disabled={isCP}
                onChange={(value) =>
                  handleFilterChange("channel_partner", value)
                }
                optionFilterProp="children"
              >
                {channelPartners.map((cp, idx) => {
                  const label =
                    typeof cp === "object" ? cp.channel_partner : cp;
                  const value =
                    typeof cp === "object" ? cp.channel_partner : cp;
                  return (
                    <Option key={value || idx} value={value}>
                      {label}
                    </Option>
                  );
                })}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6} xl={4} className="filter-col">
              <span className="filter-field-label">Status</span>
              <Select
                allowClear
                showSearch
                placeholder="Select status"
                style={{ width: "100%" }}
                value={filters.status}
                onChange={(value) => handleFilterChange("status", value)}
                optionFilterProp="children"
              >
                {statusOptions.map((status) => (
                  <Option key={status} value={status}>
                    {status}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6} xl={4} className="filter-col">
              <span className="filter-field-label">Tracking ID</span>
              <Input
                allowClear
                placeholder="Enter tracking id"
                value={filters.tracking_id}
                onChange={(e) =>
                  handleFilterChange("tracking_id", e.target.value?.trim())
                }
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6} xl={4} className="filter-col">
              <span className="filter-field-label">Movement Type</span>
              <Select
                allowClear
                placeholder="Select"
                style={{ width: "100%" }}
                value={filters.movement_type}
                onChange={(value) => handleFilterChange("movement_type", value)}
              >
                {MOVEMENT_TYPES.map((type) => (
                  <Option key={type} value={type}>
                    {type}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6} xl={4} className="filter-col">
              <span className="filter-field-label">Service Type</span>
              <Select
                allowClear
                placeholder="Select"
                style={{ width: "100%" }}
                value={filters.service_type}
                onChange={(value) => handleFilterChange("service_type", value)}
              >
                {SERVICE_TYPES.map((type) => (
                  <Option key={type} value={type}>
                    {type}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6} xl={4} className="filter-col">
              <span className="filter-field-label">Document Type</span>
              <Select
                allowClear
                placeholder="Select"
                style={{ width: "100%" }}
                value={filters.document_type}
                onChange={(value) => handleFilterChange("document_type", value)}
              >
                {DOCUMENT_TYPES.map((type) => (
                  <Option key={type} value={type}>
                    {type}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6} xl={4} className="filter-col">
              <span className="filter-field-label">Travel Type</span>
              <Select
                allowClear
                placeholder="Select"
                style={{ width: "100%" }}
                value={filters.travel_type}
                onChange={(value) => handleFilterChange("travel_type", value)}
              >
                {TRAVEL_TYPES.map((type) => (
                  <Option key={type} value={type}>
                    {type}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6} xl={4} className="filter-col">
              <span className="filter-field-label">Is Closed</span>
              <Select
                allowClear
                placeholder="Select"
                style={{ width: "100%" }}
                value={filters.is_closed}
                onChange={(value) => handleFilterChange("is_closed", value)}
              >
                {IS_CLOSED_OPTIONS.map((opt) => (
                  <Option key={opt} value={opt}>
                    {opt}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6} xl={4} className="filter-col">
              <span className="filter-field-label">Last Terminal Status</span>
              <Select
                allowClear
                placeholder="Select"
                style={{ width: "100%" }}
                value={filters.last_terminal_status}
                onChange={(value) =>
                  handleFilterChange("last_terminal_status", value)
                }
              >
                {LAST_TERMINAL_STATUS_OPTIONS.map((opt) => (
                  <Option key={opt} value={opt}>
                    {opt}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
        </div>
      </div>

      {/* ── Data Table ── */}
      <Spin spinning={loading}>
        <div className="table-panel">
          <TableContainer
            columns={columns}
            dataSource={tableData}
            pagination={{
              current: pageNum,
              pageSize: pageSize,
              total: typeof total === "number" ? total : 0,
              showSizeChanger: true,
              pageSizeOptions: ["50", "100", "150", "200"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
            onChange={(pagination) =>
              handlePageChange(pagination.current, pagination.pageSize)
            }
            scroll={{ x: "max-content" }}
            rowKey={(record) => record.tracking_id || Math.random()}
          />
        </div>
      </Spin>
    </div>
  );
};

export default CPPendancyReport;
